import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { ApiClient } from "../api";
import { UserIamDetailsResponse } from "../api/types/iam.dto";
import { ActivityTemplate } from "../api/types/schema";
import { AuthContext } from "./auth.reducer";

interface PermissionContextType {
  userIam: UserIamDetailsResponse | null;
  loading: boolean;
  error: Error | null;
  hasPermission: (permission: string) => boolean;
  hasActivityAccess: (
    activity: ActivityTemplate,
    accessType?: "read" | "write"
  ) => boolean;
  hasSensibilityAccess: (fieldSensibilityLevel?: number) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType>({
  userIam: null,
  loading: true,
  error: null,
  hasPermission: () => false,
  hasActivityAccess: () => false,
  hasSensibilityAccess: () => true,
  refreshPermissions: async () => {},
});

export const usePermissions = () => useContext(PermissionContext);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  children,
}) => {
  const {
    isAuthenticated,
    accessToken,
    isLoading: authLoading,
  } = useContext(AuthContext);
  const [userIam, setUserIam] = useState<UserIamDetailsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserIam = async () => {
    // Wait for auth flow resolution before deciding permission state
    if (authLoading) {
      setLoading(true);
      return;
    }

    // No authenticated user means no IAM permissions to load
    if (!isAuthenticated) {
      setUserIam(null);
      setError(null);
      setLoading(false);
      return;
    }

    // Authenticated but token not ready yet: keep loading to avoid false denies
    if (!accessToken) {
      setLoading(true);
      return;
    }

    const apiClient = new ApiClient({
      baseURL: import.meta.env.VITE_BACK_END_API || "",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    setLoading(true);
    let lastErr: any = null;

    // Retry up to 2 times for transient network issues
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await apiClient.iam.getCurrentUserIamDetails();
        setUserIam(response);
        setError(null);
        // Clear any existing IAM error when successful
        sessionStorage.removeItem("iamError");
        sessionStorage.removeItem("iamErrorRedirectNeeded");
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
        }
      }
    }

    try {
      console.error("Failed to fetch user IAM details after retries:", lastErr);
      const errorMessage =
        lastErr instanceof Error ? lastErr.message : "Failed to fetch user IAM details";

      // Create a standard error object
      const standardError = new Error(errorMessage);
      setError(standardError);

      // Immediately save to sessionStorage to ensure it's available
      sessionStorage.setItem(
        "iamError",
        JSON.stringify({
          message: errorMessage,
          timestamp: new Date().toISOString(),
        })
      );

      // Set flag to indicate redirect is needed
      sessionStorage.setItem("iamErrorRedirectNeeded", "true");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we're in a retry scenario from the error page
    const isRetrying = sessionStorage.getItem("iamErrorRetrying") === "true";

    if (isRetrying) {
      // Clear the retry flag
      sessionStorage.removeItem("iamErrorRetrying");
      console.log("Retrying IAM permissions fetch after error");
    }

    // Call fetchUserIam
    fetchUserIam();
  }, [isAuthenticated, accessToken, authLoading]);

  const hasPermission = (permission: string): boolean => {
    // If not authenticated, always return false
    if (!isAuthenticated || !userIam) return false;

    // Check direct permissions
    const hasDirectPermission = userIam.directPermissions?.some(
      (p) => p.code === permission
    );
    if (hasDirectPermission) return true;

    // Check permissions from roles
    const hasRolePermission = userIam.directRoles?.some((role) =>
      role.permissions?.some((p) => p.code === permission)
    );
    if (hasRolePermission) return true;

    // Check permissions from groups (via roles)
    const hasGroupRolePermission = userIam.groups?.some((group) =>
      group.roles?.some((role) =>
        role.permissions?.some((p) => p.code === permission)
      )
    );
    if (hasGroupRolePermission) return true;

    // Check direct permissions from groups
    const hasGroupDirectPermission = userIam.groups?.some((group) =>
      group.directPermissions?.some((p) => p.code === permission)
    );
    if (hasGroupDirectPermission) return true;

    return false;
  };

  const hasActivityAccess = (
    activity: ActivityTemplate,
    accessType: "read" | "write" = "read"
  ): boolean => {
    // If no IAM details available, deny access
    if (!userIam) return false;

    // If user has write access, they automatically have read access
    if (accessType === "read") {
      const hasWriteAccess = checkActivityAccess(activity, "write");
      if (hasWriteAccess) return true;
    }

    return checkActivityAccess(activity, accessType);
  };

  // Helper function to avoid recursion in hasActivityAccess
  const checkActivityAccess = (
    activity: ActivityTemplate,
    accessType: "read" | "write"
  ): boolean => {
    // Check access level - user must have sufficient level
    if (
      activity.accessLevel &&
      userIam &&
      userIam.accessLevel < activity.accessLevel
    ) {
      return false;
    }

    // If there are no specific permissions beyond access level, user has access
    if (
      !activity.permissions ||
      (!activity.permissions.users?.length &&
        !activity.permissions.roles?.length &&
        !activity.permissions.groups?.length)
    ) {
      return true;
    }

    // Check user direct permissions
    const hasUserPermission = activity.permissions.users?.some(
      (permUser: any) =>
        permUser.id === userIam?.id &&
        (accessType === "read"
          ? permUser.access === "read" || permUser.access === "write"
          : permUser.access === "write")
    );
    if (hasUserPermission) return true;

    // Check role permissions
    const hasRolePermission = activity.permissions.roles?.some((role: any) =>
      userIam?.directRoles?.some(
        (userRole: any) =>
          userRole.id === role.id &&
          (accessType === "read"
            ? role.access === "read" || role.access === "write"
            : role.access === "write")
      )
    );
    if (hasRolePermission) return true;

    // Check group permissions
    const hasGroupPermission = activity.permissions.groups?.some((group: any) =>
      userIam?.groups?.some(
        (userGroup: any) =>
          userGroup.id === group.id &&
          (accessType === "read"
            ? group.access === "read" || group.access === "write"
            : group.access === "write")
      )
    );
    if (hasGroupPermission) return true;

    return false;
  };

  const hasSensibilityAccess = (fieldSensibilityLevel?: number): boolean => {
    // If no sensibility level set on the field, allow access
    if (fieldSensibilityLevel === undefined || fieldSensibilityLevel === 0) {
      return true;
    }
    // If no user IAM details, deny access to sensitive fields
    if (!userIam) return false;
    // User must have a sensibility level >= the field's level to access it
    return (userIam.sensibilityLevel ?? 0) >= fieldSensibilityLevel;
  };

  const refreshPermissions = async () => {
    await fetchUserIam();
  };

  // Only redirect to IAM error page if:
  // 1. User is authenticated (we're not on a public route) OR we have a pending redirect
  // 2. There's an error fetching permissions
  // 3. We're not currently loading
  const needsRedirect =
    sessionStorage.getItem("iamErrorRedirectNeeded") === "true";
  if ((isAuthenticated || needsRedirect) && error && !loading) {
    // Only redirect if we're not already on the error page
    if (window.location.pathname !== "/iam-error") {
      // Clear the redirect flag to prevent redirect loops
      sessionStorage.removeItem("iamErrorRedirectNeeded");

      // Prepare error message for URL
      const errorParam = encodeURIComponent(error.message);

      // Force a FULL page reload in ALL cases by using window.location.href
      // This resolves the blank page issue by ensuring a complete refresh
      console.log("Redirecting to IAM error page");

      // Set a flag to indicate this is a forced reload
      sessionStorage.setItem("iamErrorForceReload", "true");

      // Perform the redirect with error information
      window.location.href = `/iam-error?error=${errorParam}`;

      // Return null to prevent further rendering while we're redirecting
      return null;
    }
  }

  return (
    <PermissionContext.Provider
      value={{
        userIam,
        loading,
        error,
        hasPermission,
        hasActivityAccess,
        hasSensibilityAccess,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};
